/**
 * Development helpers for testing POS med user creation locally
 * This file provides utilities to mock external service responses for local development
 */

export type MockScenario =
  | 'declined'
  | 'external_service_error'
  | 'network_error'
  | 'success'
  | 'timeout_error'
  | 'validation_error';

/**
 * Test emails that trigger different mock scenarios
 * Use these in your forms to test different response types
 */
export const TEST_EMAILS = {
  SUCCESS: 'test@success.com',
  DECLINED: 'test@declined.com',
  EXTERNAL_SERVICE_ERROR: 'test@error.com',
  NETWORK_ERROR: 'test@network.com',
  VALIDATION_ERROR: 'test@invalid.com',
  TIMEOUT_ERROR: 'test@timeout.com',
} as const;

/**
 * Determines mock scenario based on email input
 */
export const getMockScenario = (email: string): MockScenario => {
  if (email.includes('declined')) {
    return 'declined';
  }
  if (email.includes('error')) {
    return 'external_service_error';
  }
  if (email.includes('network')) {
    return 'network_error';
  }
  if (email.includes('invalid')) {
    return 'validation_error';
  }
  if (email.includes('timeout')) {
    return 'timeout_error';
  }
  return 'success';
};

/**
 * Mock responses for different scenarios
 */
export const MOCK_RESPONSES = {
  success: {
    data: {
      createPOSMedUser: {
        success: true,
        message: 'User successfully created',
      },
    },
  },
  declined: {
    data: {
      createPOSMedUser: {
        success: false,
        message: 'The certifications associated with this profile or ID is expired',
      },
    },
  },
  external_service_error: new Error('External service temporarily unavailable. Please try again later.'),
  network_error: new Error('Network error: Unable to connect to server'),
  validation_error: new Error('Validation failed: Required fields are missing or invalid'),
  timeout_error: new Error('Request timeout: The operation took too long to complete'),
};

/**
 * Simulates network delay for more realistic testing
 */
export const getSimulatedDelay = (scenario: MockScenario): number => {
  switch (scenario) {
    case 'success':
      return 800; // Normal response time
    case 'declined':
      return 1000; // Normal response time for declined
    case 'timeout_error':
      return 5000; // Long delay to simulate timeout
    case 'network_error':
      return 100; // Quick failure
    default:
      return 1200; // Moderate delay for errors
  }
};

/**
 * Main mock function that handles all scenarios
 */
export const createMockPOSMedUserResponse = async (email: string): Promise<typeof MOCK_RESPONSES.success> => {
  const scenario = getMockScenario(email);
  const delay = getSimulatedDelay(scenario);

  console.log(`🔧 Mock POS Med User Creation - Scenario: ${scenario}, Delay: ${delay}ms`);

  // Simulate network delay
  await new Promise((resolve) => setTimeout(resolve, delay));

  const response = MOCK_RESPONSES[scenario];

  if (response instanceof Error) {
    throw response;
  }

  return response;
};

/**
 * Development guide for testing different scenarios
 */
export const DEV_TESTING_GUIDE = `
🧪 POS Med User Creation - Local Testing Guide

To test different scenarios locally, use these email patterns in your forms:

✅ SUCCESS CASE:
   Email: test@success.com (or any email without trigger words)
   
❌ DECLINED CASE:
   Email: test@declined.com
   
❌ ERROR CASES:
   External Service Error: test@error.com
   Network Error: test@network.com  
   Validation Error: test@invalid.com
   Timeout Error: test@timeout.com

📝 Environment Variables:
   Set REACT_APP_MOCK_POS_MED_USER=false to use real API in development
   
🔧 To enable/disable mocking:
   // In your .env.local file
   REACT_APP_MOCK_POS_MED_USER=true  # Enable mocking (default in dev)
   REACT_APP_MOCK_POS_MED_USER=false # Use real API
`;

// Log the guide in development
// if (process.env.NODE_ENV === 'development') {
//   console.log(DEV_TESTING_GUIDE);
// }
